import * as React from "react";
import Dialog from "@mui/material/Dialog";
import CloseIcon from "@mui/icons-material/Close";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import {
  Box,
  IconButton,
  Skeleton,
  Typography,
  useMediaQuery,
  useTheme,
  alpha,
} from "@mui/material";
import ViewDetails from "../genericComponents/view-details/ViewDetails";
import { BasicProgram, ViewDetailsServices } from "@/app/classes/main";
import { SearchServiceMobileCard } from "./SearchServiceMobileCard";
import MaxServiceAlert from "../genericComponents/card/MaxServiceAlert";
import ViewDetailsRsOneContainer from "../genericComponents/view-details/ViewDetailsRsOneContainer";
import { SEARCH } from "./searchUi";

interface SearchServicesProps {
  openSearchDialog: any;
  handleCloseSearchDialog: any;
  searchServiceList: any;
  setSearchServiceList: any;
  setServiceName: any;
  setReloadCartService: any;
  searchQuery?: string;
  isLoading?: boolean;
}

const ResultSkeleton = () => (
  <Box
    sx={{
      mb: 1.5,
      borderRadius: SEARCH.radiusMd,
      border: `1px solid ${SEARCH.border}`,
      p: 2,
      bgcolor: alpha(SEARCH.primary, 0.03),
      "&:last-of-type": { mb: 0 },
    }}
  >
    <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
      <Skeleton variant="rounded" width={88} height={88} sx={{ borderRadius: 2 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Skeleton width="70%" height={22} sx={{ mb: 1 }} />
        <Skeleton width="45%" height={16} sx={{ mb: 1.5 }} />
        <Skeleton width="40%" height={20} />
      </Box>
    </Box>
    <Skeleton sx={{ mt: 1.5 }} height={14} />
    <Skeleton width="90%" height={14} sx={{ mt: 0.5 }} />
  </Box>
);

const SearchServices: React.FC<SearchServicesProps> = ({
  openSearchDialog,
  handleCloseSearchDialog,
  searchServiceList,
  setSearchServiceList,
  setServiceName,
  setReloadCartService,
  searchQuery = "",
  isLoading = false,
}) => {
  const [open, setOpen] = React.useState(false);
  const [viewDetails, setViewDetails] = React.useState<
    ViewDetailsServices | undefined
  >(undefined);
  const [reloadViewDetailsItem, setReloadViewDetailsItem] =
    React.useState(false);
  const [updateCard, setUpdateCard] = React.useState(false);
  const [openMaxService, setOpenMaxService] = React.useState(false);
  const [openRsOneDetails, setOpenRsOneDetails] = React.useState(false);
  const [viewDetailsRsOneSid, setViewDetailsRsOneSid] = React.useState<
    number | undefined
  >(undefined);
  const theme = useTheme();
  const screenWidth = useMediaQuery(theme.breakpoints.down("sm"));

  const handleOpenViewDetailsRsOne = (basicProgram: BasicProgram) => {
    setOpenRsOneDetails(true);
    setViewDetailsRsOneSid(basicProgram?.id);
  };

  const handleOpenViewCart = (item: any) => {
    setOpen(true);
    setViewDetails(item);
  };

  const handleCloseDialog = () => {
    handleCloseSearchDialog();
    setSearchServiceList([]);
    setServiceName("");
  };

  const count = Array.isArray(searchServiceList) ? searchServiceList.length : 0;
  const showEmpty =
    !isLoading &&
    openSearchDialog &&
    (!searchServiceList || searchServiceList.length === 0);

  return (
    <React.Fragment>
      <Dialog
        open={openSearchDialog}
        onClose={handleCloseDialog}
        aria-labelledby="search-results-dialog-title"
        aria-describedby="search-results-dialog-description"
        scroll={screenWidth ? "paper" : "body"}
        maxWidth="md"
        fullWidth
        closeAfterTransition={false}
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, sm: SEARCH.radiusLg },
            overflow: "hidden",
            maxHeight: { xs: "100%", sm: "min(92vh, 760px)" },
            border: `1px solid ${SEARCH.border}`,
            boxShadow: "0 28px 80px rgba(16, 33, 23, 0.14)",
            bgcolor: SEARCH.surface,
          },
        }}
        sx={{
          display: { xs: "none", sm: "block" },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            maxHeight: { xs: "100vh", sm: "min(92vh, 760px)" },
          }}
        >
          <Box
            sx={{
              flexShrink: 0,
              background: `linear-gradient(125deg, ${alpha(SEARCH.primary, 0.12)} 0%, ${alpha(
                "#f2c46c",
                0.15
              )} 55%, ${alpha(SEARCH.primary, 0.06)} 100%)`,
              borderBottom: `1px solid ${alpha(SEARCH.primary, 0.12)}`,
              px: 2.5,
              py: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: 1.5,
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  id="search-results-dialog-title"
                  sx={{
                    fontFamily: SEARCH.fontDisplay,
                    fontWeight: 700,
                    fontSize: "1.35rem",
                    letterSpacing: "-0.03em",
                    color: SEARCH.text,
                    lineHeight: 1.2,
                  }}
                >
                  Find your service
                </Typography>
                <Typography
                  id="search-results-dialog-description"
                  sx={{
                    mt: 0.35,
                    fontSize: "0.88rem",
                    color: SEARCH.muted,
                    lineHeight: 1.45,
                  }}
                >
                  {searchQuery ? (
                    <>
                      Results for{" "}
                      <Box component="span" sx={{ fontWeight: 800, color: SEARCH.primaryDark }}>
                        “{searchQuery}”
                      </Box>
                      {!isLoading && count > 0 ? (
                        <Box component="span" sx={{ color: SEARCH.mutedLight }}>
                          {" "}
                          · {count} {count === 1 ? "match" : "matches"}
                        </Box>
                      ) : null}
                    </>
                  ) : (
                    "Browse matching services and add to your cart."
                  )}
                </Typography>
              </Box>
              <IconButton
                onClick={handleCloseDialog}
                aria-label="Close search results"
                size="small"
                sx={{
                  color: SEARCH.primary,
                  bgcolor: alpha(SEARCH.primary, 0.1),
                  "&:hover": { bgcolor: alpha(SEARCH.primary, 0.18) },
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>

          <Box
            sx={{
              overflow: "auto",
              px: 2,
              py: 2,
              flex: 1,
              "&::-webkit-scrollbar": { width: 6 },
              "&::-webkit-scrollbar-thumb": {
                borderRadius: 999,
                background: alpha(SEARCH.primary, 0.25),
              },
            }}
          >
            {isLoading ? (
              <>
                <ResultSkeleton />
                <ResultSkeleton />
                <ResultSkeleton />
              </>
            ) : showEmpty ? (
              <Box
                sx={{
                  py: 6,
                  px: 2,
                  textAlign: "center",
                  maxWidth: 360,
                  mx: "auto",
                }}
              >
                <SearchOffIcon
                  sx={{
                    fontSize: 52,
                    color: alpha(SEARCH.primary, 0.35),
                    mb: 1.5,
                  }}
                  aria-hidden
                />
                <Typography
                  sx={{
                    fontFamily: SEARCH.fontDisplay,
                    fontWeight: 700,
                    fontSize: "1.15rem",
                    color: SEARCH.text,
                    mb: 0.75,
                  }}
                >
                  No services found
                </Typography>
                <Typography sx={{ fontSize: "0.9rem", color: SEARCH.muted, lineHeight: 1.5 }}>
                  Try a different keyword — e.g. facial, waxing, or hair spa.
                </Typography>
              </Box>
            ) : (
              searchServiceList?.map((item: any) => (
                <Box
                  key={item?.id}
                  onClick={() => handleOpenViewCart(item)}
                  sx={{
                    cursor: "pointer",
                    mb: 1.5,
                    borderRadius: SEARCH.radiusMd,
                    border: `1px solid ${SEARCH.border}`,
                    overflow: "hidden",
                    bgcolor: SEARCH.surfaceElevated,
                    boxShadow: SEARCH.shadowCard,
                    transition: "box-shadow 0.2s ease, transform 0.2s ease",
                    "&:hover": {
                      boxShadow: SEARCH.shadowCardHover,
                      transform: "translateY(-2px)",
                      borderColor: alpha(SEARCH.primary, 0.28),
                    },
                    "&:last-of-type": { mb: 0 },
                  }}
                >
                  <SearchServiceMobileCard
                    item={item}
                    setReloadViewDetailsItem={(value: any) => {
                      setReloadViewDetailsItem(value);
                      setReloadCartService();
                    }}
                    updateCard={updateCard}
                    setUpdateCard={() => setUpdateCard(false)}
                    setOpenMaxService={setOpenMaxService}
                    handleOpenViewDetailsRsOne={(item: any) =>
                      handleOpenViewDetailsRsOne(item)
                    }
                  />
                </Box>
              ))
            )}
          </Box>
        </Box>
      </Dialog>
      <ViewDetails
        open={open}
        setClose={() => setOpen(false)}
        imageUrl={
          viewDetails?.banner?.secureHttpPath ??
          viewDetails?.coverImage?.secureHttpPath
        }
        heading={viewDetails?.name}
        servicedDetails={viewDetails?.aboutList}
        descriptions={viewDetails?.descriptions}
        amount={viewDetails?.amount}
        min={viewDetails?.min}
        id={viewDetails?.id}
        cid={viewDetails?.cid}
        subHeading={viewDetails?.subHeading}
        mrp={viewDetails?.mrp}
        specialPrice={viewDetails?.specialPrice}
        specialMessage={viewDetails?.specialMessage}
        coin={viewDetails?.cashbackSalontymCoins}
        aboutList={viewDetails?.aboutList}
        serviceCharge={viewDetails?.serviceCharge}
        productCost={viewDetails?.productCost}
        reloadCartItemsFlag={() => {
          setUpdateCard(true), setReloadCartService();
        }}
        reloadViewDetailsItem={reloadViewDetailsItem}
        setReloadViewDetailsItem={() => {
          setReloadViewDetailsItem(false);
        }}
      />
      <MaxServiceAlert
        openInfo={openMaxService}
        handleClose={() => setOpenMaxService(false)}
      />
      <ViewDetailsRsOneContainer
        open={openRsOneDetails}
        handleCloseStoreDialog={() => setOpenRsOneDetails(false)}
        viewDetailsRsOneSid={viewDetailsRsOneSid}
      />
    </React.Fragment>
  );
};

export default SearchServices;
